/* eslint-disable jira/filename-case */
import React, { type UIEvent } from 'react';
import { type WithIntlProps, defineMessage } from '@atlassian/jira-intl';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import Button from '@atlaskit/button/button';
import ButtonGroup from '@atlaskit/button/button-group';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import Spinner from '@atlaskit/spinner/spinner';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl/src/index.tsx';
import { useDeleteChangeActions } from '../../controllers/delete-change-actions/index.tsx';

const deleteMessage: { id: string; defaultMessage: string; description: string } = defineMessage({
	id: 'directories.filters-v2.delete-modal.footer.delete',
	defaultMessage: 'Delete',
	description:
		'Label for the delete button that is located at the footer of the delete modal. Clicking this button will delete the filter.',
});

const cancelButtonCaption: { id: string; defaultMessage: string; description: string } =
	defineMessage({
		id: 'directories.filters-v2.delete-modal.footer.cancel-button-caption',
		defaultMessage: 'Cancel',
		description:
			'Button that cancels all forms of action modals, e.g. to create, edit, or delete an entity',
	});

type FooterProps = {
	canDelete?: boolean;
	deleteInProgress?: boolean;
	onCancel?: (arg1: undefined) => void;
	onDelete?: (onComplete: (() => void) | undefined, arg1: UIEvent, arg2: UIAnalyticsEvent) => void;
	intl: IntlShape;
};

const Footer = ({
	intl: { formatMessage },
	canDelete = true,
	deleteInProgress = false,
	onCancel = noop,
	onDelete = noop,
}: FooterProps) => {
	const { publish } = useDeleteChangeActions();

	const onDeleteWithEvent = (
		event: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		// additional args aside from event & analyticsEvent that you wish to pass down needs to be passed as the first argument
		onDelete(publish, event, analyticsEvent);
	};

	return (
		<ModalFooter>
			<Container>
				{deleteInProgress && (
					<Box xcss={spinnerContainerStyles}>
						<Spinner size="small" />
					</Box>
				)}
				<ButtonGroup>
					<Button
						appearance="danger"
						type="submit"
						isDisabled={!canDelete}
						onClick={onDeleteWithEvent}
					>
						{formatMessage(deleteMessage)}
					</Button>
					<Button
						appearance="subtle-link"
						isDisabled={deleteInProgress}
						// @ts-expect-error - TS2322 - Type '(arg1: undefined) => void' is not assignable to type '() => void'
						onClick={onCancel}
					>
						{formatMessage(cancelButtonCaption)}
					</Button>
				</ButtonGroup>
			</Container>
		</ModalFooter>
	);
};

const DeleteFiltersModalFooterView: React.FC<WithIntlProps<FooterProps>> & {
	WrappedComponent: React.ComponentType<FooterProps>;
} = injectIntl(Footer);
export default DeleteFiltersModalFooterView;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	flexGrow: 1,
});

const spinnerContainerStyles = xcss({
	marginRight: 'space.200',
});
